import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TypographySize } from 'mm-theme-configuration/dist/consts';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { useTheme } from '../../../../../theming/useTheme';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { getLineHeightBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { MoreButton } from './MoreButton';
import { SocialSectionDataProps } from '../../footer/AdditionalFooterSections/FooterSocialLinks';
import { SocialLinks } from './SocialLinks';
import { AdditionalStyleSheet } from '../../../../components.utils';
import { WebFontsContext } from '../../../../../theming/ThemeProviderWithFonts';
import { HUGE_SCREEN_SIZE, MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { Logo, LogoDataProps } from './Logo';
import { InjectedVideoLogoApiProps, VideoLogo } from './VideoLogo';
import { LinkButtonDataProps } from '../../utils';
import { FixedNav } from './FixedNav';
import { Menu } from './menu/Menu';
import { componentConfigThemeKey, HeaderWithSubMenusThemeProps } from './HeaderWithSubMenus.theme';
import { Editions } from './Editions';
import { SubMenu } from './SubMenu';
import { HeaderAnimation, HeaderAnimationContext } from '../HeaderAnimation';
import { useHeaderHeights } from './useHeaderHeights';
import { AnimationDirection, AnimationDirectionType } from '../menuAnimations.util';
import { reducedHeaderFirstRowHeightFactors } from '../header.utils';
import { ScrollableNav } from './ScrollableNav';
import { NavigationLink } from '../primaryHeader/PrimaryHeader';
import { ScrollableNavDropdown } from './ScrollableNavDropdown';
import { MenuLinkDataProps } from '../primaryHeader/menu/links/MenuSubLink';
import { useSubMenu, SubMenuReducerProps } from './subMenuReducer';
import { useLinksWithSubMenuHover } from './linkWithSubHeader.utils';

interface MenuData {
  links: MenuLinkDataProps[];
  buttons?: Array<LinkButtonDataProps>;
}

export interface HeaderWithSubMenusDataProps {
  links: NavigationLink[] | null;
  logo: LogoDataProps;
  logoImageLarge: string | null;
  logoImageSmall: string | null;
  editions: NavigationLink[] | null;
  menu: MenuData | null;
  videoLogo: InjectedVideoLogoApiProps | null;
  pagePath: string;
  editionEndpoint: string;
  socialSection: SocialSectionDataProps | null;
  moreButtonText: string;
}

interface MoreButtonState {
  offset: number;
  links: HTMLElement[];
}

const MORE_BUTTON_ACTION_TYPE = 'SetMoreButtonFinalData' as const;
interface MoreButtonAction {
  type: typeof MORE_BUTTON_ACTION_TYPE;
  data: MoreButtonState;
}

const getStyles = (themeProps: HeaderWithSubMenusThemeProps, menuFoldState: AnimationDirectionType, animationDuration: number) => {
  const {
    firstRowBackgroundColor,
    baseUnit,
    firstRowHeightInLargeScreen,
    firstRowHeightInMediumScreen,
    firstRowHeightInSmallScreen,
    verticalGapInLargeScreen,
    verticalGapInMediumScreen,
    verticalGapInSmallScreen,
    fontSizesStyles,
    linkFontSizeLarge,
    firstRowBoxShadow,
  } = themeProps;

  const largeScreenBaseUnitFactored = menuFoldState === AnimationDirection.UP ? firstRowHeightInLargeScreen * reducedHeaderFirstRowHeightFactors.large * baseUnit + baseUnit : firstRowHeightInLargeScreen * baseUnit;

  return StyleSheet.create({
    header: {
      position: 'fixed',
      width: '100%',
      zIndex: 1500,
      top: 0,
    },
    headerFirstRow: {
      boxShadow: firstRowBoxShadow,
      backgroundColor: firstRowBackgroundColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      zIndex: 100,
      position: 'relative',
      transition: `height ${animationDuration}ms ease 0s`,
      [MEDIA_BREAKPOINTS.large]: {
        height: menuFoldState === AnimationDirection.UP ? `${firstRowHeightInLargeScreen * reducedHeaderFirstRowHeightFactors.large * baseUnit}px` : `${firstRowHeightInLargeScreen * baseUnit}px`,
        padding: `0 max(${baseUnit * verticalGapInLargeScreen}px, calc((100% - ${HUGE_SCREEN_SIZE}px) / 2))`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${firstRowHeightInMediumScreen * baseUnit}px`,
        padding: `0 ${baseUnit * verticalGapInMediumScreen}px`,
        justifyContent: 'space-between',
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${firstRowHeightInSmallScreen * baseUnit}px`,
        padding: `0 ${baseUnit * verticalGapInSmallScreen}px`,
        justifyContent: 'space-between',
      },
    },
    headerFirstRowItemsContainer: {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
    },
    fixedNav: {
      [MEDIA_BREAKPOINTS.large]: {
        height: `${getLineHeightBySize(fontSizesStyles, linkFontSizeLarge, 'large')}px`,
        overflow: 'hidden',
        display: 'block',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none',
      },
    },
    subMenuWrapper: {
      position: 'absolute',
      left: 0,
      top: `${largeScreenBaseUnitFactored - baseUnit * 2}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none',
      },
    },
  });
};

const showMoreButtonStyle = (buttonOffset: number, shouldShowMoreButton: boolean): AdditionalStyleSheet => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.large]: {
        display: shouldShowMoreButton ? 'flex' : 'none',
        transform: `translateX(-${buttonOffset}px)`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none',
      },
    },
  }) as AdditionalStyleSheet;
};

const createMenuStyle = (baseUnit: number, verticalGapInMediumScreen: number, verticalGapInSmallScreen: number): AdditionalStyleSheet => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'inline-flex',
        marginRight: `${baseUnit * verticalGapInMediumScreen}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'inline-flex',
        marginRight: `${baseUnit * verticalGapInSmallScreen}px`,
      },
    },
  }) as AdditionalStyleSheet;
};

const createHeaderStyleWithAnimation = (animationDuration: number) => {
  return StyleSheet.create({
    foldingUp: {
      [MEDIA_BREAKPOINTS.medium]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`,
      },
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(0)',
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(0)',
      },
    },
  });
};

const getInvisibleLinks = (parent: HTMLUListElement | null) => {
  if (!parent) return [];
  const baseline = parent.getBoundingClientRect().top;
  return Array.from(parent.childNodes).filter(child => {
    return (child as HTMLElement).getBoundingClientRect().top > baseline;
  }) as HTMLElement[];
};

const getButtonOffset = (listElement: HTMLUListElement, moreButtonOriginalPosition: number, baseUnit: BaseUnit, verticalGap: number) => {
  return moreButtonOriginalPosition - listElement.getBoundingClientRect().right - (baseUnit * verticalGap);
};

const getMoreMenuLinks = (invisibleLinks: ChildNode[], links: NavigationLink[]) => {
  const numLinks = invisibleLinks.length;
  return links.slice(links.length - numLinks);
};

const getHeaderStyleWithAnimation = (menuFoldState: AnimationDirectionType, animationDuration: number) => {
  const style = createHeaderStyleWithAnimation(animationDuration);
  return menuFoldState === AnimationDirection.UP ? style.foldingUp : style.foldingDown;
};

const isSubMenuOpen = (subMenuState: SubMenuReducerProps) => {
  return subMenuState.element !== null;
};

const getChildrenByIndex = (links: NavigationLink[] | null, index: number) => {
  if (!links || !links[index] || !links[index].children) return [];

  return links[index].children || [];
};

const moreButtonInitialState: MoreButtonState = {
  offset: 0,
  links: [],
};

const moreButtonReducer = (state: MoreButtonState, action: MoreButtonAction) => {
  if (action.type === MORE_BUTTON_ACTION_TYPE) {
    return {
      offset: action.data.offset,
      links: action.data.links,
    };
  }
  return state;
};

const createMoreButtonAction = (data: MoreButtonState) => ({
  type: MORE_BUTTON_ACTION_TYPE,
  data,
});

export const HeaderWithSubMenusComponent: React.FunctionComponent<HeaderWithSubMenusDataProps> = props => {
  const { videoLogo, logo, links, moreButtonText, socialSection, pagePath, menu, editions, editionEndpoint, logoImageLarge, logoImageSmall } = props;
  const { menuFoldState, animationDuration } = React.useContext<HeaderAnimationContext>(HeaderAnimationContext);
  const themeProps = useTheme<HeaderWithSubMenusThemeProps>(componentConfigThemeKey);

  const {
    firstRowLinkColorTypeActive,
    firstRowLinkColorTypeNormal,
    firstRowLinkColorTypeHover,
    firstRowLinkCurrentColor,
    secondRowLinkColorTypeActive,
    secondRowLinkColorTypeHover,
    secondRowLinkColorTypeNormal,
    secondRowLinkCurrentColor,
    baseUnit,
    fontSizesStyles,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall,
    logoHeightInLargeScreen,
    logoHeightInMediumScreen,
    logoHeightInSmallScreen,
    verticalGapInLargeScreen,
    verticalGapInMediumScreen,
    verticalGapInSmallScreen,
    linkDropdownIconColor,
    firstRowHeightInLargeScreen,
    firstRowHeightInMediumScreen,
    firstRowHeightInSmallScreen,
    subMenuBackgroundColor,
    secondRowBackgroundColor,
    secondRowHeightInMediumScreen,
    secondRowHeightInSmallScreen,
    editionsSeparatorColor,
    editionsMenuBackgroundColor,
    firstRowBackgroundColor,
    subMenuLinksFontSize,
    secondRowBoxShadow,
  } = themeProps;
  const ulRef = React.useRef<HTMLUListElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [moreButtonOriginalPosition, setMoreButtonOriginalPosition] = React.useState(-1);
  const [moreButtonState, dispatchMoreButtonState] = React.useReducer(moreButtonReducer, moreButtonInitialState);
  const [subMenuState, dispatchMenuState] = useSubMenu();
  const [scrollableMenuIndex, setScrollableMenuIndex] = React.useState(-1);
  const navHeightLarge = getLineHeightBySize(fontSizesStyles, linkFontSizeLarge, 'large');
  const styles = getStyles(themeProps, menuFoldState, animationDuration);
  const shouldShowSocial = socialSection && socialSection.imageLinks.length > 0;
  const shouldEditions = editions && editions.length > 0;
  const webFontsContext = React.useContext(WebFontsContext);

  const indexOfFirstInvisibleLink = links!.length - moreButtonState.links.length;
  const shouldShowMoreButton = moreButtonState.links.length > 0;
  const moreButtonStyle = showMoreButtonStyle(moreButtonState.offset, shouldShowMoreButton);
  const menuStyle = createMenuStyle(baseUnit, verticalGapInMediumScreen, verticalGapInSmallScreen);

  React.useEffect(() => {
    if (buttonRef.current && moreButtonOriginalPosition === -1 && shouldShowMoreButton) {
      const { left } = buttonRef.current.getBoundingClientRect();
      if (left > 0) {
        setMoreButtonOriginalPosition(left);
      }
    }
  }, [buttonRef, moreButtonOriginalPosition, setMoreButtonOriginalPosition, shouldShowMoreButton]);

  React.useEffect(() => {
    if (ulRef.current !== null && buttonRef.current !== null) {
      if (menuFoldState === AnimationDirection.NONE) {
        const invisibleLinks = getInvisibleLinks(ulRef.current);
        if (invisibleLinks.length > 0) {
          const offset = getButtonOffset(ulRef.current, moreButtonOriginalPosition, baseUnit, verticalGapInLargeScreen);
          dispatchMoreButtonState(createMoreButtonAction({ offset, links: invisibleLinks }));
        }
      }
    }
  }, [ulRef, buttonRef, webFontsContext, baseUnit, moreButtonOriginalPosition, verticalGapInLargeScreen, menuFoldState]);

  const closeSubMenu = () => {
    dispatchMenuState({
      data: {
        element: null,
        links: null,
        index: -1,
        linkFontSize: null,
        backgroundColor: '',
      },
    });

    setScrollableMenuIndex(-1);
  };

  React.useEffect(() => {
    window.addEventListener('scroll', closeSubMenu);

    return () => window.removeEventListener('scroll', closeSubMenu);
  });

  const toggleSubMenu = (element: HTMLElement, navigationLinks: NavigationLink[] | null, index: number, linkFontSize: TypographySize, backgroundColor: string) => {
    if (navigationLinks) {
      dispatchMenuState({
        data: {
          element,
          links: navigationLinks,
          index,
          linkFontSize,
          backgroundColor,
        },
      });
    }
  };

  const onToggleMoreLinksMenu = () => {
    const moreButtonLinks = getMoreMenuLinks(moreButtonState.links, links!);
    toggleSubMenu(buttonRef.current!, moreButtonLinks, -1, linkFontSizeLarge, firstRowBackgroundColor);
  };

  const onToggleFixedNavMenu = (element: HTMLElement, index: number) => {
    if (index === -1) {
      const moreButtonLinks = getMoreMenuLinks(moreButtonState.links, links!);
      toggleSubMenu(buttonRef.current!, moreButtonLinks, -1, linkFontSizeLarge, firstRowBackgroundColor);
    } else {
      const fixedNavLinks = links![index].children;
      toggleSubMenu(element, fixedNavLinks, index, subMenuLinksFontSize, subMenuBackgroundColor);
    }
  };

  const onScrollableNavMenuToggle = (element: HTMLElement, index: number) => {
    if (!links) return;
    if (index !== scrollableMenuIndex) {
      setScrollableMenuIndex(index);
    } else {
      setScrollableMenuIndex(-1);
    }
  };

  const factoredFirstRowHeight = menuFoldState === AnimationDirection.UP ? firstRowHeightInLargeScreen * reducedHeaderFirstRowHeightFactors.large : firstRowHeightInLargeScreen;
  const [registerLinkWithSubMenu, getHoverPlaceholders] = useLinksWithSubMenuHover(onToggleFixedNavMenu, ulRef, factoredFirstRowHeight, baseUnit);
  if (buttonRef.current) {
    registerLinkWithSubMenu(buttonRef, -1);
  }

  return (
    <React.Fragment>
      <header className={css(getHeaderStyleWithAnimation(menuFoldState, animationDuration), styles.header)}>
        <div className={css(styles.headerFirstRow)}>
          <div className={css(styles.headerFirstRowItemsContainer)}>
            {menu ? <Menu links={menu.links} editions={editions} editionEndpoint={editionEndpoint} buttons={menu.buttons} additionalStyle={menuStyle} /> : null}
            {videoLogo
              ? (
                <VideoLogo
                  {...videoLogo}
                  href={logo.href}
                  firstRowHeightInSmallScreen={firstRowHeightInSmallScreen}
                  firstRowHeightInMediumScreen={firstRowHeightInMediumScreen}
                  firstRowHeightInLargeScreen={firstRowHeightInLargeScreen}
                  menuAnimation={menuFoldState}
                  reduceHeightAnimationDuration={animationDuration}
                />
              )
              : (
                <Logo
                  {...logo}
                  logoHeightInLargeScreen={logoHeightInLargeScreen}
                  logoHeightInMediumScreen={logoHeightInMediumScreen}
                  logoHeightInSmallScreen={logoHeightInSmallScreen}
                  menuAnimation={menuFoldState}
                  reduceHeightAnimationDuration={animationDuration}
                  logoImageLarge={logoImageLarge}
                  logoImageSmall={logoImageSmall}
                />
              )}
            <FixedNav
              rowHeight={firstRowHeightInLargeScreen}
              navHeightLarge={navHeightLarge}
              ulRef={ulRef}
              links={links}
              pagePath={pagePath}
              activeStateColor={firstRowLinkColorTypeActive}
              hoverStateColor={firstRowLinkColorTypeHover}
              idleColor={firstRowLinkColorTypeNormal}
              linkFontSizeLarge={linkFontSizeLarge}
              linkFontSizeMedium={linkFontSizeMedium}
              linkFontSizeSmall={linkFontSizeSmall}
              currentLinkColor={firstRowLinkCurrentColor}
              linkDropdownIconColor={linkDropdownIconColor}
              subMenuState={subMenuState}
              indexOfFirstInvisibleLink={indexOfFirstInvisibleLink}
              linkBackgroundColor={subMenuState.backgroundColor}
              registerLinkWithSubMenu={registerLinkWithSubMenu}
            />
            <MoreButton
              refObject={buttonRef}
              moreButtonText={moreButtonText}
              additionalStyle={moreButtonStyle}
              fontSizeSmall={linkFontSizeSmall}
              fontSizeMedium={linkFontSizeMedium}
              fontSizeLarge={linkFontSizeLarge}
              colorTypeHover={firstRowLinkColorTypeHover}
              colorTypeNormal={firstRowLinkColorTypeNormal}
              colorTypeActive={firstRowLinkColorTypeActive}
              dropdownIconColor={linkDropdownIconColor}
              onToggleMenu={onToggleMoreLinksMenu}
              isMenuOpen={subMenuState.element === buttonRef.current}
            />
          </div>
          <div className={css(styles.headerFirstRowItemsContainer)}>
            {shouldShowSocial && (
              <SocialLinks
                socialLinks={socialSection!.imageLinks}
                verticalGapInLargeScreen={verticalGapInLargeScreen}
                verticalGapInMediumScreen={verticalGapInMediumScreen}
                menuAnimation={menuFoldState}
                reduceHeightAnimationDuration={animationDuration}
              />
            )}
            {shouldEditions && (
              <Editions
                editions={editions!}
                editionEndpoint={editionEndpoint}
                verticalGapInLargeScreen={verticalGapInLargeScreen}
                verticalGapInMediumScreen={verticalGapInMediumScreen}
                verticalGapInSmallScreen={verticalGapInSmallScreen}
                fontSizeSmall={linkFontSizeSmall}
                fontSizeMedium={linkFontSizeMedium}
                fontSizeLarge={linkFontSizeLarge}
                colorTypeHover={firstRowLinkColorTypeHover}
                colorTypeNormal={firstRowLinkColorTypeNormal}
                colorTypeActive={firstRowLinkColorTypeActive}
                dropdownIconColor={linkDropdownIconColor}
                editionsSeparatorColor={editionsSeparatorColor}
                editionsMenuBackgroundColor={editionsMenuBackgroundColor}
              />
            )}
          </div>
          <div className={css(styles.subMenuWrapper)} onMouseLeave={closeSubMenu}>
            {getHoverPlaceholders()}
            {
              isSubMenuOpen(subMenuState) && (
                <SubMenu
                  menuFoldState={menuFoldState}
                  animationDuration={animationDuration}
                  links={subMenuState.links!}
                  parentElement={subMenuState.element!}
                  activeStateColor={firstRowLinkColorTypeActive}
                  hoverStateColor={firstRowLinkColorTypeHover}
                  idleColor={firstRowLinkColorTypeNormal}
                  currentLinkColor={firstRowLinkCurrentColor}
                  pagePath={pagePath}
                  linkFontSize={subMenuState.linkFontSize!}
                  innerLinkFontSize={subMenuLinksFontSize}
                  backgroundColor={subMenuState.backgroundColor}
                  innerLinksBackgroundColor={subMenuBackgroundColor}
                  shouldOffsetLeft={false}
                  openLinkIndex={subMenuState.index}
                  linkDropdownIconColor={linkDropdownIconColor}
                />
              )
            }
          </div>
        </div>
        <ScrollableNav
          menuFoldState={menuFoldState}
          animationDuration={animationDuration}
          links={links}
          linkColorTypeActive={secondRowLinkColorTypeActive}
          linkColorTypeHover={secondRowLinkColorTypeHover}
          linkColorTypeNormal={secondRowLinkColorTypeNormal}
          currentLinkColor={secondRowLinkCurrentColor}
          pagePath={pagePath}
          baseUnit={baseUnit}
          linkFontSizeLarge={linkFontSizeLarge}
          linkFontSizeMedium={linkFontSizeMedium}
          linkFontSizeSmall={linkFontSizeSmall}
          verticalGapInLargeScreen={verticalGapInLargeScreen}
          verticalGapInMediumScreen={verticalGapInMediumScreen}
          verticalGapInSmallScreen={verticalGapInSmallScreen}
          secondRowBackgroundColor={secondRowBackgroundColor}
          secondRowHeightInMediumScreen={secondRowHeightInMediumScreen}
          secondRowHeightInSmallScreen={secondRowHeightInSmallScreen}
          linkDropdownIconColor={linkDropdownIconColor}
          openLinkIndex={scrollableMenuIndex}
          onMenuToggle={onScrollableNavMenuToggle}
          boxShadow={secondRowBoxShadow}
        />
      </header>
      {
        scrollableMenuIndex >= 0 && (
          <ScrollableNavDropdown
            firstRowHeightInMediumScreen={firstRowHeightInMediumScreen}
            firstRowHeightInSmallScreen={firstRowHeightInSmallScreen}
            secondRowHeightInMediumScreen={secondRowHeightInMediumScreen}
            secondRowHeightInSmallScreen={secondRowHeightInSmallScreen}
            backgroundColor={subMenuBackgroundColor}
            links={getChildrenByIndex(links, scrollableMenuIndex)}
            pagePath={pagePath}
            linkColorTypeActive={secondRowLinkColorTypeActive}
            linkColorTypeHover={secondRowLinkColorTypeHover}
            linkColorTypeNormal={secondRowLinkColorTypeNormal}
            currentLinkColor={secondRowLinkCurrentColor}
            linkFontSize={subMenuLinksFontSize}
          />
        )
      }
    </React.Fragment>
  );
};

export const HeaderWithSubMenus: React.FunctionComponent<HeaderWithSubMenusDataProps> = props => {
  const headerHeights = useHeaderHeights();
  return (
    <ErrorBoundary>
      <HeaderAnimation headerHeights={headerHeights}>
        <HeaderWithSubMenusComponent {...props} />
      </HeaderAnimation>
    </ErrorBoundary>
  );
};
